import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { REQUEST_TYPES } from "@/lib/crmConstants";
import { RequestTypeBadge } from "@/components/Badges";
import { Copy, Download, ExternalLink, FileCode } from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

// ---------- Shared CSS + JS scaffolding ------------------------------------
const SHARED_CSS = `
.hem-form { display:grid; gap:12px; max-width:640px; font-family:system-ui,-apple-system,sans-serif; color:#0A0A0A; }
.hem-form label { font-size:12px; letter-spacing:.06em; text-transform:uppercase; color:#52525B; font-weight:500; }
.hem-form input, .hem-form textarea, .hem-form select {
  width:100%; padding:10px 12px; border:1px solid #E5E5E5; border-radius:6px; font-size:14px; box-sizing:border-box; background:#fff;
}
.hem-form input:focus, .hem-form textarea:focus, .hem-form select:focus { outline:none; border-color:#4F46E5; box-shadow:0 0 0 3px rgba(79,70,229,.15); }
.hem-form .hem-row-2 { display:grid; grid-template-columns: 1fr 1fr; gap:12px; }
.hem-form button.hem-submit { padding:12px 20px; background:#4F46E5; color:#fff; border:none; border-radius:6px; font-weight:600; font-size:14px; cursor:pointer; margin-top:6px; }
.hem-form button.hem-submit:hover { background:#4338CA; }
.hem-form button.hem-submit:disabled { opacity:.6; cursor:wait; }
.hem-status { font-size:14px; margin-top:8px; padding:10px 12px; border-radius:6px; display:none; }
.hem-status.ok { display:block; background:#F0FDF4; color:#166534; border:1px solid #86EFAC; }
.hem-status.err { display:block; background:#FEF2F2; color:#991B1B; border:1px solid #FCA5A5; }
.hem-hint { font-size:11px; color:#71717A; margin-top:-4px; }
.hem-form fieldset { border:1px solid #E5E5E5; border-radius:8px; padding:16px; margin:0; }
.hem-form legend { font-size:12px; text-transform:uppercase; letter-spacing:.06em; color:#4F46E5; padding:0 6px; font-weight:600; }
`;

const RENDER_TICKET_JS = `
function hemShowTicket(root, body) {
  var status = root.querySelector('.hem-status');
  status.className = 'hem-status ok';
  status.replaceChildren();
  var strong = document.createElement('strong');
  strong.textContent = 'Thanks! ';
  var span = document.createElement('span');
  span.textContent = 'We received your submission. Your ticket ID is ';
  var code = document.createElement('code');
  code.style.padding = '2px 6px';
  code.style.background = '#DCFCE7';
  code.style.borderRadius = '4px';
  code.textContent = body.ticket_id;
  var tail = document.createElement('span');
  tail.textContent = '. A WhatsApp confirmation is on its way.';
  status.appendChild(strong); status.appendChild(span); status.appendChild(code); status.appendChild(tail);
}
function hemShowError(root, msg) {
  var status = root.querySelector('.hem-status');
  status.className = 'hem-status err';
  status.textContent = msg || 'Something went wrong. Please try again.';
}
`;

// ---------- 1a. Contact Us -------------------------------------------------
const CONTACT_US_SNIPPET = `<!-- HERO ECO MED — Contact Us form (embed in Shopify Custom Liquid) -->
<div id="hem-contact-us">
  <form class="hem-form" id="hem-contact-form" novalidate enctype="multipart/form-data">
    <div>
      <label>Enquiry Type *</label>
      <select name="enquiry_type" id="hem-enquiry-type" required>
        <option value="">Select an option…</option>
        <option>General Enquiry</option>
        <option>Complaint / Service Issue</option>
        <option>Suggestion / Feedback</option>
        <option>Looking for a New Product</option>
        <option>Bulk / Institutional Order</option>
        <option>Dealer Enquiry</option>
      </select>
    </div>

    <div class="hem-row-2">
      <div>
        <label>Full Name *</label>
        <input type="text" name="full_name" required />
      </div>
      <div>
        <label>Mobile Number *</label>
        <input type="tel" name="phone" pattern="[0-9+ ]{10,15}" required />
      </div>
    </div>
    <div class="hem-row-2">
      <div>
        <label>Email Address *</label>
        <input type="email" name="email" required />
      </div>
      <div>
        <label>State *</label>
        <select name="city_state" id="hem-state" required></select>
      </div>
    </div>

    <!-- Complaint-only fields -->
    <fieldset id="hem-complaint-fields" style="display:none">
      <legend>Complaint details</legend>
      <div class="hem-row-2">
        <div>
          <label>Product Category</label>
          <select name="product_category" id="hem-complaint-cat"></select>
        </div>
        <div>
          <label>Product Model No.</label>
          <input type="text" name="product_model" placeholder="Type your product model" />
        </div>
      </div>
      <div class="hem-row-2" style="margin-top:12px">
        <div>
          <label>Serial Number</label>
          <input type="text" name="serial_number" placeholder="HEM-XXXX-XXXX" />
        </div>
        <div>
          <label>Order / Invoice Number</label>
          <input type="text" name="order_number" />
        </div>
      </div>
      <div class="hem-row-2" style="margin-top:12px">
        <div>
          <label>Date of Purchase</label>
          <input type="date" name="purchase_date" />
        </div>
        <div>
          <label>Upload Photo / Video (JPG, PNG, MP4 · max 10MB)</label>
          <input type="file" name="attachment" accept="image/jpeg,image/png,video/mp4" />
        </div>
      </div>
    </fieldset>

    <!-- New-product-only fields -->
    <fieldset id="hem-lead-fields" style="display:none">
      <legend>Product interest</legend>
      <div class="hem-row-2">
        <div>
          <label>Product Category Interested In</label>
          <input type="text" name="product_category_interest" placeholder="e.g., Wheelchairs, Hospital Beds, Walkers, Mattresses" />
        </div>
        <div>
          <label>Preferred Budget</label>
          <select name="budget">
            <option value="">Select…</option>
            <option>&lt; ₹10,000</option>
            <option>₹10,000 – ₹25,000</option>
            <option>₹25,000 – ₹50,000</option>
            <option>₹50,000 – ₹1,00,000</option>
            <option>&gt; ₹1,00,000</option>
          </select>
        </div>
      </div>
    </fieldset>

    <!-- Feedback has no extra fields -->


    <div>
      <label>Message / Details *</label>
      <textarea name="message" rows="4" required></textarea>
    </div>

    <button type="submit" class="hem-submit">Send Message</button>
    <div class="hem-status" role="status" aria-live="polite"></div>
  </form>
</div>

<style>${SHARED_CSS}</style>

<script>
(function(){
  var API = '${API_BASE}';
  var root = document.getElementById('hem-contact-us');
  var form = document.getElementById('hem-contact-form');
  var typeSel = document.getElementById('hem-enquiry-type');
  var stateSel = document.getElementById('hem-state');
  var complaintBox = document.getElementById('hem-complaint-fields');
  var leadBox = document.getElementById('hem-lead-fields');
  var complaintCat = document.getElementById('hem-complaint-cat');

  // 1. Populate States
  fetch(API + '/meta/states').then(function(r){return r.json();}).then(function(list){
    var opts = '<option value="">Select state…</option>';
    list.forEach(function(s){ opts += '<option>' + s + '</option>'; });
    stateSel.innerHTML = opts;
  });

  // 2. Populate Category dropdown (complaint only — model is free text now)
  fetch(API + '/meta/categories').then(function(r){return r.json();}).then(function(cats){
    var opts = '<option value="">Select…</option>';
    cats.forEach(function(c){ opts += '<option>' + c + '</option>'; });
    complaintCat.innerHTML = opts;
  });

  // 3. Enquiry type routing & conditional field visibility
  typeSel.addEventListener('change', function(){
    var v = typeSel.value;
    if (v === 'Bulk / Institutional Order') { window.location.href = '/pages/bulk-procurement'; return; }
    if (v === 'Dealer Enquiry') { window.location.href = '/pages/become-our-dealer'; return; }
    complaintBox.style.display = (v === 'Complaint / Service Issue') ? 'block' : 'none';
    leadBox.style.display = (v === 'Looking for a New Product') ? 'block' : 'none';
  });

  ${RENDER_TICKET_JS}

  form.addEventListener('submit', async function(e){
    e.preventDefault();
    var btn = form.querySelector('.hem-submit');
    btn.disabled = true; btn.textContent = 'Sending…';
    try {
      var fd = new FormData(form);
      var res = await fetch(API + '/public/forms/contact_us', { method:'POST', body: fd });
      var body = await res.json();
      if (!res.ok) throw new Error((body.detail || 'Submission failed'));
      hemShowTicket(root, body);
      form.reset();
      complaintBox.style.display='none'; leadBox.style.display='none';
    } catch(err) {
      hemShowError(root, err.message);
    } finally {
      btn.disabled = false; btn.textContent = 'Send Message';
    }
  });
})();
</script>
`;

// ---------- 1b. Bulk Procurement -------------------------------------------
const BULK_SNIPPET = `<!-- HERO ECO MED — Bulk Procurement form -->
<div id="hem-bulk">
  <form class="hem-form" id="hem-bulk-form" novalidate enctype="multipart/form-data">
    <div class="hem-row-2">
      <div><label>Organisation / Institution Name *</label><input type="text" name="organisation_name" required></div>
      <div><label>Type of Institution *</label>
        <select name="institution_type" required>
          <option value="">Select…</option>
          <option>Hospital</option><option>Nursing Home</option><option>Rehab Centre</option>
          <option>Government Tender</option><option>Distributor</option><option>Corporate CSR</option><option>Other</option>
        </select>
      </div>
    </div>
    <div class="hem-row-2">
      <div><label>Contact Person Name *</label><input type="text" name="contact_person" required></div>
      <div><label>Designation *</label><input type="text" name="designation" required></div>
    </div>
    <div class="hem-row-2">
      <div><label>Mobile Number *</label><input type="tel" name="phone" required></div>
      <div><label>Email Address *</label><input type="email" name="email" required></div>
    </div>
    <div class="hem-row-2">
      <div><label>State *</label><select name="city_state" id="hem-bulk-state" required></select></div>
      <div><label>Estimated Quantity *</label><input type="number" name="estimated_quantity" min="1" required></div>
    </div>
    <div>
      <label>Products Required *</label>
      <input type="text" name="products_required" placeholder="e.g., Wheelchairs, Hospital Beds, Walkers" required />
    </div>
    <div class="hem-row-2">
      <div><label>Required By Date</label><input type="date" name="required_by"></div>
      <div><label>GST Number</label><input type="text" name="gst_number"></div>
    </div>
    <div>
      <label>Tender Reference Number (government tenders only)</label>
      <input type="text" name="tender_reference">
    </div>
    <div>
      <label>Additional Requirements / Specifications</label>
      <textarea name="additional_requirements" rows="3"></textarea>
    </div>
    <div>
      <label>Upload Tender Document / RFP (PDF · max 10MB)</label>
      <input type="file" name="attachment" accept="application/pdf">
    </div>
    <button type="submit" class="hem-submit">Request a Quote</button>
    <div class="hem-status" role="status" aria-live="polite"></div>
  </form>
</div>

<style>${SHARED_CSS}</style>

<script>
(function(){
  var API = '${API_BASE}';
  var root = document.getElementById('hem-bulk');
  var form = document.getElementById('hem-bulk-form');
  var stateSel = document.getElementById('hem-bulk-state');

  fetch(API + '/meta/states').then(function(r){return r.json();}).then(function(list){
    var opts = '<option value="">Select state…</option>';
    list.forEach(function(s){ opts += '<option>' + s + '</option>'; });
    stateSel.innerHTML = opts;
  });

  ${RENDER_TICKET_JS}

  form.addEventListener('submit', async function(e){
    e.preventDefault();
    var btn = form.querySelector('.hem-submit');
    btn.disabled = true; btn.textContent = 'Sending…';
    try {
      var fd = new FormData(form);
      var res = await fetch(API + '/public/forms/bulk_procurement', { method:'POST', body: fd });
      var body = await res.json();
      if (!res.ok) throw new Error(body.detail || 'Submission failed');
      hemShowTicket(root, body);
      form.reset();
    } catch(err) {
      hemShowError(root, err.message);
    } finally {
      btn.disabled = false; btn.textContent = 'Request a Quote';
    }
  });
})();
</script>
`;

// ---------- 1c. CSR Connect ------------------------------------------------
const CSR_SNIPPET = `<!-- HERO ECO MED — CSR Connect form -->
<div id="hem-csr">
  <form class="hem-form" id="hem-csr-form" novalidate enctype="multipart/form-data">
    <div><label>Organisation Name *</label><input type="text" name="organisation_name" required></div>
    <div class="hem-row-2">
      <div><label>CSR Contact Person Name *</label><input type="text" name="contact_person" required></div>
      <div><label>Designation *</label><input type="text" name="designation" required></div>
    </div>
    <div class="hem-row-2">
      <div><label>Mobile Number *</label><input type="tel" name="phone" required></div>
      <div><label>Email Address *</label><input type="email" name="email" required></div>
    </div>
    <div>
      <label>Organisation Website</label>
      <input type="url" name="website" placeholder="https://…">
    </div>
    <div>
      <label>Type of Partnership *</label>
      <select name="partnership_type" required>
        <option value="">Select…</option>
        <option>Equipment Donation</option>
        <option>Funding Support</option>
        <option>Awareness Drive</option>
        <option>Distribution Partnership</option>
        <option>Other</option>
      </select>
    </div>
    <div>
      <label>Beneficiary Details *</label>
      <textarea name="beneficiary_details" rows="3" placeholder="Who is this for & approx. number of beneficiaries" required></textarea>
    </div>
    <div>
      <label>Preferred Geography / Location *</label>
      <input type="text" name="preferred_geography" required>
    </div>
    <div>
      <label>Message</label>
      <textarea name="message" rows="3"></textarea>
    </div>
    <button type="submit" class="hem-submit">Start a Conversation</button>
    <div class="hem-status" role="status" aria-live="polite"></div>
  </form>
</div>

<style>${SHARED_CSS}</style>

<script>
(function(){
  var API = '${API_BASE}';
  var root = document.getElementById('hem-csr');
  var form = document.getElementById('hem-csr-form');
  ${RENDER_TICKET_JS}
  form.addEventListener('submit', async function(e){
    e.preventDefault();
    var btn = form.querySelector('.hem-submit');
    btn.disabled = true; btn.textContent = 'Sending…';
    try {
      var fd = new FormData(form);
      var res = await fetch(API + '/public/forms/csr_connect', { method:'POST', body: fd });
      var body = await res.json();
      if (!res.ok) throw new Error(body.detail || 'Submission failed');
      hemShowTicket(root, body);
      form.reset();
    } catch(err) {
      hemShowError(root, err.message);
    } finally {
      btn.disabled = false; btn.textContent = 'Start a Conversation';
    }
  });
})();
</script>
`;

// ---------- 1d. Become Our Dealer ------------------------------------------
const DEALER_SNIPPET = `<!-- HERO ECO MED — Become Our Dealer form -->
<div id="hem-dealer">
  <form class="hem-form" id="hem-dealer-form" novalidate>
    <div class="hem-row-2">
      <div><label>Full Name *</label><input type="text" name="full_name" required></div>
      <div><label>Business / Firm Name *</label><input type="text" name="business_name" required></div>
    </div>
    <div class="hem-row-2">
      <div><label>Mobile Number *</label><input type="tel" name="phone" required></div>
      <div><label>Email Address *</label><input type="email" name="email" required></div>
    </div>
    <div class="hem-row-2">
      <div><label>State *</label><select name="city_state" id="hem-dealer-state" required></select></div>
      <div><label>Nearest City / Town of Operation *</label><input type="text" name="nearest_city" required></div>
    </div>
    <div class="hem-row-2">
      <div><label>Years in Business *</label><input type="number" name="years_in_business" min="0" required></div>
      <div><label>Current Business Type *</label>
        <select name="current_business_type" required>
          <option value="">Select…</option>
          <option>Medical Equipment Store</option>
          <option>Pharmacy</option>
          <option>General Trading</option>
          <option>New Business</option>
          <option>Other</option>
        </select>
      </div>
    </div>
    <div>
      <label>Do you currently sell mobility / medical equipment? *</label>
      <select name="currently_sells" id="hem-dealer-sells" required>
        <option value="">Select…</option>
        <option>Yes</option>
        <option>No</option>
      </select>
    </div>
    <div id="hem-dealer-brands-wrap" style="display:none">
      <label>Brands currently dealing in</label>
      <input type="text" name="current_brands">
    </div>
    <div class="hem-row-2">
      <div><label>GST Number *</label><input type="text" name="gst_number" required></div>
      <div><label>Shop / Showroom Area (sq. ft.)</label><input type="number" name="shop_area_sqft" min="0"></div>
    </div>
    <div>
      <label>Investment Capacity</label>
      <select name="investment_capacity">
        <option value="">Select…</option>
        <option>&lt; ₹2 lakh</option>
        <option>₹2 – 5 lakh</option>
        <option>₹5 – 10 lakh</option>
        <option>₹10 – 25 lakh</option>
        <option>&gt; ₹25 lakh</option>
      </select>
    </div>
    <div><label>Message / Additional Info</label><textarea name="message" rows="3"></textarea></div>
    <button type="submit" class="hem-submit">Apply to Become a Dealer</button>
    <div class="hem-status" role="status" aria-live="polite"></div>
  </form>
</div>

<style>${SHARED_CSS}</style>

<script>
(function(){
  var API = '${API_BASE}';
  var root = document.getElementById('hem-dealer');
  var form = document.getElementById('hem-dealer-form');
  var stateSel = document.getElementById('hem-dealer-state');
  var sellsSel = document.getElementById('hem-dealer-sells');
  var brandsWrap = document.getElementById('hem-dealer-brands-wrap');

  fetch(API + '/meta/states').then(function(r){return r.json();}).then(function(list){
    var opts = '<option value="">Select state…</option>';
    list.forEach(function(s){ opts += '<option>' + s + '</option>'; });
    stateSel.innerHTML = opts;
  });

  sellsSel.addEventListener('change', function(){
    brandsWrap.style.display = (sellsSel.value === 'Yes') ? 'block' : 'none';
  });

  ${RENDER_TICKET_JS}

  form.addEventListener('submit', async function(e){
    e.preventDefault();
    var btn = form.querySelector('.hem-submit');
    btn.disabled = true; btn.textContent = 'Sending…';
    try {
      var data = Object.fromEntries(new FormData(form).entries());
      var res = await fetch(API + '/public/forms/become_dealer', {
        method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(data)
      });
      var body = await res.json();
      if (!res.ok) throw new Error(body.detail || 'Submission failed');
      hemShowTicket(root, body);
      form.reset();
      brandsWrap.style.display = 'none';
    } catch(err) {
      hemShowError(root, err.message);
    } finally {
      btn.disabled = false; btn.textContent = 'Apply to Become a Dealer';
    }
  });
})();
</script>
`;

// ---------- 1e. Product Registration ---------------------------------------
const WARRANTY_SNIPPET = `<!-- HERO ECO MED — Product Registration (SOP-compliant) -->
<div id="hem-warranty">
  <form class="hem-form" id="hem-warranty-form" novalidate enctype="multipart/form-data">
    <div style="display:flex;gap:8px;align-items:center;background:#F3F4F6;padding:10px 12px;border-radius:6px">
      <span style="font-size:12px;text-transform:uppercase;letter-spacing:.06em;color:#52525B">I am a:</span>
      <label style="display:flex;gap:6px;align-items:center;cursor:pointer;font-size:14px">
        <input type="radio" name="is_salesperson_toggle" value="false" checked /> Customer
      </label>
      <label style="display:flex;gap:6px;align-items:center;cursor:pointer;font-size:14px">
        <input type="radio" name="is_salesperson_toggle" value="true" /> Salesperson
      </label>
    </div>
    <fieldset>
      <legend>Equipment details</legend>
      <div class="hem-row-2">
        <div><label>Product Category *</label><select name="product_category" id="hem-wc-cat" required></select></div>
        <div><label>Product Model *</label><input type="text" name="product_model" placeholder="Type your product model" required /></div>
      </div>
      <div class="hem-row-2" style="margin-top:12px">
        <div><label>Serial Number * <span title="Format: HEM-XXXX-XXXX" style="cursor:help;color:#4F46E5">ⓘ</span></label><input type="text" name="serial_number" placeholder="HEM-XXXX-XXXX" pattern="^HEM-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}$" required></div>
        <div><label>Date of Purchase *</label><input type="date" name="purchase_date" required></div>
      </div>
      <div style="margin-top:12px">
        <label>Upload Invoice (PDF / JPG / PNG · max 5MB)</label>
        <input type="file" name="attachment" accept="application/pdf,image/jpeg,image/png">
      </div>
    </fieldset>
    <fieldset style="margin-top:12px">
      <legend>Buyer details</legend>
      <div class="hem-row-2">
        <div><label>Full Name *</label><input type="text" name="full_name" required></div>
        <div><label>Mobile Number * <span class="hem-hint">(10-digit)</span></label><input type="tel" name="phone" inputmode="numeric" pattern="[0-9]{10,15}" placeholder="10-digit mobile number" required></div>
      </div>
      <div class="hem-row-2" style="margin-top:12px">
        <div><label>Email Address *</label><input type="email" name="email" placeholder="you@example.com" required></div>
        <div><label>Dealer / Store Purchased From</label><input type="text" name="dealer" placeholder="Store / Dealer name"></div>
      </div>
      <div style="margin-top:12px">
        <label>Invoice / Bill Number</label>
        <input type="text" name="invoice_number" placeholder="Enter invoice or bill number">
      </div>
    </fieldset>
    <fieldset id="hem-sp-fields" style="margin-top:12px;display:none;background:#FEF3C7;border-color:#FCD34D">
      <legend style="color:#92400E">Salesperson details</legend>
      <div class="hem-row-2">
        <div><label>Unique Product ID *</label><input type="text" name="unique_product_id" placeholder="Enter unique product ID"></div>
        <div><label>Unique Salesperson ID *</label><input type="text" name="unique_salesperson_id" placeholder="Enter unique salesperson ID"></div>
      </div>
      <div style="margin-top:12px">
        <label>Selling Price (₹)</label>
        <input type="number" name="selling_price" min="0" step="1" placeholder="Enter selling price">
      </div>
    </fieldset>
    <div style="margin-top:12px;display:flex;gap:8px;align-items:flex-start">
      <input type="checkbox" name="consent" id="hem-consent" required style="margin-top:4px" />
      <label for="hem-consent" style="text-transform:none;letter-spacing:0;color:#0A0A0A;font-size:13px;line-height:1.5">
        I hereby consent to Hero Eco Med collecting, processing, and securely storing my personal information for the purpose of responding to my enquiry, providing product recommendations, customer support, and related services. I have read and agree to the Privacy Policy.
      </label>
    </div>
    <button type="submit" class="hem-submit">Register Product</button>
    <div class="hem-status" role="status" aria-live="polite"></div>
  </form>
</div>

<style>${SHARED_CSS}</style>

<script>
(function(){
  var API = '${API_BASE}';
  var root = document.getElementById('hem-warranty');
  var form = document.getElementById('hem-warranty-form');
  var cat = document.getElementById('hem-wc-cat');
  var spBox = document.getElementById('hem-sp-fields');
  var toggles = form.querySelectorAll('input[name="is_salesperson_toggle"]');

  fetch(API + '/meta/categories').then(function(r){return r.json();}).then(function(cats){
    cat.innerHTML = '<option value="">Select category…</option>' + cats.map(function(c){ return '<option>' + c + '</option>'; }).join('');
  });

  function syncSalesperson(){
    var v = form.querySelector('input[name="is_salesperson_toggle"]:checked').value === 'true';
    spBox.style.display = v ? 'block' : 'none';
    form.querySelectorAll('#hem-sp-fields input').forEach(function(i){ i.required = v && i.name !== 'selling_price'; });
  }
  toggles.forEach(function(t){ t.addEventListener('change', syncSalesperson); });

  ${RENDER_TICKET_JS}

  form.addEventListener('submit', async function(e){
    e.preventDefault();
    var btn = form.querySelector('.hem-submit');
    btn.disabled = true; btn.textContent = 'Registering…';
    try {
      var fd = new FormData(form);
      fd.set('is_salesperson', form.querySelector('input[name="is_salesperson_toggle"]:checked').value);
      fd.delete('is_salesperson_toggle');
      fd.set('consent', form.querySelector('#hem-consent').checked ? 'true' : 'false');
      var res = await fetch(API + '/public/forms/product_registration', { method:'POST', body: fd });
      var body = await res.json();
      if (!res.ok) throw new Error(body.detail || 'Submission failed');
      hemShowTicket(root, body);
      form.reset();
      syncSalesperson();
    } catch(err) {
      hemShowError(root, err.message);
    } finally {
      btn.disabled = false; btn.textContent = 'Register Product';
    }
  });
})();
</script>
`;

const README = `# Hero Eco Med CRM — Website Forms Handoff

Five drop-in HTML/CSS/JS snippets, one per intake form. Paste each into a
Shopify "Custom Liquid" section on the corresponding page.

## Endpoints (all under ${API_BASE})

| Form                    | Method | Endpoint                              | Body                       |
|-------------------------|--------|---------------------------------------|----------------------------|
| Contact Us              | POST   | /public/forms/contact_us              | multipart/form-data        |
| Bulk Procurement        | POST   | /public/forms/bulk_procurement        | multipart/form-data        |
| CSR Connect             | POST   | /public/forms/csr_connect             | multipart/form-data        |
| Become Our Dealer       | POST   | /public/forms/become_dealer           | application/json (no file) |
| Product Registration    | POST   | /public/forms/product_registration    | multipart/form-data        |

### Success response (200)
{
  "ticket_id": "HEM-LEAD-AB12CD",
  "id": "<uuid>",
  "stage": "New lead",
  "request_type": "contact_us_lead"
}

### Error response (4xx)
{ "detail": "Missing required fields: phone, email" }

## Helper endpoints (public, no auth)
- GET /meta/states                       → list of Indian states + UTs (populate state dropdown)
- GET /meta/categories                   → 7 product categories (populate Category dropdown)
- GET /meta/products?category=Wheelchair → list of models in that category (cascading Model dropdown)

## Source tagging
The backend automatically tags submissions to /public/forms/* as \`source=website\`.
Manual entries made by staff inside the CRM console are tagged \`source=manual\`.
No work needed on the front-end for this.

## Displaying the ticket ID
After a successful submit, the snippet renders the returned ticket ID inline in
a green success box. The customer will also receive a WhatsApp confirmation
carrying the same ticket ID, and they can WhatsApp it back to your CRM number
at any time for an automatic status reply.

## Contact Us — special behaviour
The Enquiry Type dropdown routes as follows:
- General Enquiry / Suggestion / Complaint / Looking for a New Product → posts to contact_us endpoint.
- Bulk / Institutional Order → JS redirects to /pages/bulk-procurement.
- Dealer Enquiry               → JS redirects to /pages/become-our-dealer.

The Complaint sub-fields (Product Model, Serial Number, Order/Invoice, Purchase Date,
photo/video upload) show only when Complaint is selected; the New-Product fields
(Product Category, Preferred Budget) show only when "Looking for a New Product"
is selected. All conditional logic is fully client-side, no server round-trip.

## Cascading Category → Model
Contact Us (Complaint) and Product Registration both feature a two-step
Category → Model dropdown. When the customer picks a category, the Model select
fetches /meta/products?category=... and re-populates. Model is disabled until a
category is chosen.

## File uploads
- Contact Us complaint: JPG / PNG / MP4, max 10MB (field name: attachment)
- Bulk Procurement: PDF, max 10MB (field name: attachment)
- CSR Connect: PDF, max 10MB (field name: attachment)
- Product Registration: PDF / JPG / PNG, max 5MB (field name: attachment)

Files are uploaded to the CRM via multipart/form-data alongside the text fields.
The backend stores them in managed object storage and links each file to the
newly-created record; staff can download from the record detail page inside the
CRM.

## Error surfacing
Every snippet renders errors inline in a red box below the submit button. The
error message shown is whatever the backend returns in \`detail\`. No alerts,
no modals. Screen-reader announced via \`role="status" aria-live="polite"\`.

## Territory-based routing (backend, no frontend work needed)
When a Contact Us submission is a Lead (New Product), the backend reads the
"State" field and auto-assigns the record to the Sales person mapped to that
state. Same for Complaints → Service person. Unmapped states fall to the
team lead / shared queue. This all happens server-side — the site developer
does not need to know or care.
`;

// ---------- Handoff page component -----------------------------------------
const FORMS = {
  contact_us: { snippet: CONTACT_US_SNIPPET, mappedType: "contact_us_lead", label: "Contact Us" },
  bulk_procurement: { snippet: BULK_SNIPPET, mappedType: "bulk_procurement", label: "Bulk Procurement" },
  csr_connect: { snippet: CSR_SNIPPET, mappedType: "csr", label: "CSR Connect" },
  become_dealer: { snippet: DEALER_SNIPPET, mappedType: "dealership", label: "Become Our Dealer" },
  product_registration: { snippet: WARRANTY_SNIPPET, mappedType: "warranty", label: "Product Registration" },
};

function CodeBlock({ code, title, testId }) {
  return (
    <div className="card-shell overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5]">
        <div className="flex items-center gap-2">
          <FileCode className="w-4 h-4 text-[#4F46E5]" />
          <span className="data-label">{title}</span>
        </div>
        <Button size="sm" variant="outline" onClick={() => {
          navigator.clipboard.writeText(code);
          toast.success("Copied to clipboard");
        }} data-testid={`copy-${testId}`}>
          <Copy className="w-3.5 h-3.5 mr-1" /> Copy
        </Button>
      </div>
      <pre className="p-4 text-xs font-mono overflow-auto max-h-[500px] bg-[#0A0A0A] text-[#E5E5E5]" data-testid={`code-${testId}`}>
        <code>{code}</code>
      </pre>
    </div>
  );
}

export default function HandoffPage() {
  const [active, setActive] = useState("contact_us");
  const cfg = FORMS[active];

  const downloadBundle = () => {
    const parts = [`README.md\n===\n${README}\n\n`];
    Object.entries(FORMS).forEach(([k, v]) => {
      parts.push(`--- forms/${k}.html ---\n${v.snippet}\n\n`);
    });
    const blob = new Blob([parts.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "hero-eco-med-forms-handoff.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-8 space-y-6 max-w-6xl">
      <div className="flex items-end justify-between">
        <div>
          <div className="data-label mb-1">Developer handoff</div>
          <h1 className="font-heading font-black text-4xl tracking-tight">Website forms bundle</h1>
          <p className="text-sm text-[#52525B] mt-2 max-w-2xl">
            Five drop-in HTML/CSS/JS snippets for the Hero Eco Med Shopify site. Each submits to the CRM backend,
            returns a ticket ID, and triggers a WhatsApp confirmation to the customer.
            Cascading Category→Model dropdowns are populated live from the Shopify catalogue.
          </p>
        </div>
        <Button onClick={downloadBundle} className="bg-[#4F46E5] hover:bg-[#4338CA] text-white" data-testid="download-bundle">
          <Download className="w-4 h-4 mr-1" /> Download bundle (.txt)
        </Button>
      </div>

      <div className="card-shell p-6">
        <div className="data-label mb-2">API endpoint pattern</div>
        <div className="font-mono text-sm bg-[#0A0A0A] text-[#E5E5E5] p-3 rounded flex items-center justify-between">
          <span>POST {API_BASE}/public/forms/&#123;form_key&#125;</span>
          <Button size="sm" variant="outline" className="text-white border-[#3F3F46]" onClick={() => window.open(`${API_BASE}/`, "_blank")} data-testid="open-api">
            <ExternalLink className="w-3.5 h-3.5 mr-1" /> Health check
          </Button>
        </div>
        <div className="mt-3 text-xs text-[#52525B] font-mono">
          form_key ∈ &#123; contact_us · bulk_procurement · csr_connect · become_dealer · product_registration &#125;
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {Object.entries(FORMS).map(([k, v]) => (
          <button
            key={k}
            onClick={() => setActive(k)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
              active === k
                ? "bg-[#4F46E5] text-white border-[#4F46E5]"
                : "bg-white text-[#3F3F46] border-[#E5E5E5] hover:bg-[#F4F4F5]"
            }`}
            data-testid={`handoff-tab-${k}`}
          >
            <RequestTypeBadge requestType={v.mappedType} />
          </button>
        ))}
      </div>

      <CodeBlock code={cfg.snippet} title={`forms/${active}.html — ${cfg.label}`} testId={active} />

      <CodeBlock code={README} title="README.md" testId="readme" />
    </div>
  );
}
